import React, { useEffect, useState } from 'react';
import { 
  Container,
  Typography, 
  Box, 
  Card, 
  CardContent, 
  TextField, 
  Button, 
  CircularProgress, 
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  Grid,
  Paper,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  CheckCircle,
  Refresh as RefreshIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';
import api from '../services/api';

const DomainIntegration: React.FC = () => {
  const { agencySlug } = useParams<{ agencySlug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { agency } = useAuth();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [showWizard, setShowWizard] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [officeDetails, setOfficeDetails] = useState<any>(null);
  const [feedStatus, setFeedStatus] = useState<any>(null);
  const [loadingFeedStatus, setLoadingFeedStatus] = useState(false);

  useEffect(() => {
    if (!agency?.slug) return;
    
    // Check for OAuth callback
    const connected = searchParams.get('connected');
    const errorParam = searchParams.get('error');
    
    if (connected === 'true') {
      setSuccess('Successfully connected to Domain.com.au!');
      fetchOfficeDetails();
      fetchFeedStatus();
      setActiveStep(4); // Move to final step
    } else if (errorParam) {
      setError(decodeURIComponent(errorParam));
    }
    
    const fetchConfig = async () => {
      try {
        setLoading(true);
        
        // Check if we should show wizard (no connection or incomplete)
        const officeResponse = await api.get(`/agencies/${agency.slug}/domain-office-details`).catch(() => null);
        if (officeResponse?.data) {
          setOfficeDetails(officeResponse.data);
          if (!officeResponse.data.connected) {
            setShowWizard(true);
          }
        } else {
          setShowWizard(true);
        }
      } catch (err: any) {
        // Config might not exist yet, that's okay
        if (err.response?.status !== 404) {
          console.error('Error loading domain config:', err);
        } else {
          setShowWizard(true);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
    fetchFeedStatus();
  }, [agency?.slug, searchParams]);

  const fetchOfficeDetails = async () => {
    if (!agency?.slug) return;
    try {
      const response = await api.get(`/agencies/${agency.slug}/domain-office-details`);
      setOfficeDetails(response.data);
    } catch (err: any) {
      console.error('Error loading office details:', err);
    }
  };

  const fetchFeedStatus = async () => {
    if (!agency?.slug) return;
    try {
      setLoadingFeedStatus(true);
      const response = await api.get(`/agencies/${agency.slug}/domain-feed-status`);
      setFeedStatus(response.data);
    } catch (err: any) {
      console.error('Error loading feed status:', err);
    } finally {
      setLoadingFeedStatus(false);
    }
  };

  const handleConnectDomain = async () => {
    if (!agency?.slug) return;
    
    setConnecting(true);
    setError('');
    
    try {
      const response = await api.get(`/agencies/${agency.slug}/domain-oauth/authorize`);
      if (response.data.authUrl) {
        // Redirect to Domain OAuth page
        window.location.href = response.data.authUrl;
      } else {
        setError('Failed to initiate Domain connection');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to connect to Domain.com.au');
      setConnecting(false);
    }
  };

  const handleReconnect = async () => {
    if (!agency?.slug) return;
    
    setConnecting(true);
    setError('');
    
    try {
      const response = await api.post(`/agencies/${agency.slug}/domain-oauth/reconnect`);
      if (response.data.authUrl) {
        // Redirect to Domain OAuth page
        window.location.href = response.data.authUrl;
      } else {
        setError('Failed to reconnect to Domain');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reconnect to Domain.com.au');
      setConnecting(false);
    }
  };


  if (!agency) return null;

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Card>
          <CardContent>
            <CircularProgress />
          </CardContent>
        </Card>
      </Container>
    );
  }

  const steps = [
    {
      label: 'Connect to Domain.com.au',
      description: 'Connect your Workflow.Estate account to your Domain.com.au agency profile. This connection allows automatic listing publishing to Domain.',
    },
    {
      label: 'Authorize Access',
      description: 'You will be redirected to Domain.com.au to authorize Workflow.Estate to manage your property listings.',
    },
    {
      label: 'Verify Connection',
      description: 'We\'ll fetch your agency details from Domain.com.au to verify the connection.',
    },
    {
      label: 'Go Live',
      description: 'Your Domain.com.au integration is active! Listings will automatically sync.',
    },
  ];

  const formatTimeUntilExpiry = (expiresAt: Date | null) => {
    if (!expiresAt) return 'Unknown';
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = expiry.getTime() - now.getTime();
    
    if (diff < 0) return 'Expired';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton 
          onClick={() => navigate(`/agencies/${agencySlug}/third-party-integrations`)}
          sx={{ mr: 2 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          Domain.com.au Integration
        </Typography>
      </Box>

      {showWizard && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Domain Integration Setup Wizard
              </Typography>
              {officeDetails?.connected ? (
                <Button 
                  variant="contained" 
                  color="success"
                  onClick={() => {
                    setShowWizard(false);
                    // Optionally navigate back to integrations list
                    navigate(`/agencies/${agencySlug}/third-party-integrations`);
                  }}
                >
                  Done
                </Button>
              ) : (
                <Button size="small" onClick={() => setShowWizard(false)}>
                  Skip Wizard
                </Button>
              )}
            </Box>
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel>{step.label}</StepLabel>
                  <StepContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {step.description}
                    </Typography>
                    {index === 0 && (
                      <Button
                        variant="contained"
                        startIcon={<LinkIcon />}
                        onClick={handleConnectDomain}
                        disabled={connecting}
                        sx={{ mb: 2 }}
                      >
                        {connecting ? 'Connecting...' : 'Connect My Agency to Domain.com.au'}
                      </Button>
                    )}
                    {index === 1 && (
                      <Typography variant="body2" color="text.secondary">
                        You will be redirected to Domain.com.au to authorize access. After authorization, you'll be redirected back to Workflow.Estate.
                      </Typography>
                    )}
                    {index === 2 && officeDetails?.connected && (
                      <Box sx={{ mt: 2 }}>
                        <Chip 
                          icon={<CheckCircle />} 
                          label="Connected" 
                          color="success" 
                          sx={{ mb: 2 }}
                        />
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Agency Name
                            </Typography>
                            <Typography variant="body1">
                              {officeDetails.agencyName || 'Not available'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Office ID
                            </Typography>
                            <Typography variant="body1">
                              {officeDetails.officeId || 'Not available'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                    {index === 3 && officeDetails?.connected && (
                      <Box sx={{ mt: 2 }}>
                        <Chip 
                          icon={<CheckCircle />} 
                          label="Domain Sync: Enabled" 
                          color="success" 
                          sx={{ mb: 2 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          Your Domain.com.au integration is active! Listings will automatically sync when you create or update properties.
                        </Typography>
                      </Box>
                    )}
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </CardContent>
        </Card>
      )}

      {/* Agency Verification Card */}
      {officeDetails?.connected && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Agency Verification
              </Typography>
              <Chip 
                icon={<CheckCircle />} 
                label="Connected" 
                color="success" 
                size="small"
              />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Agency Verified
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {officeDetails.agencyName || 'Not available'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Office ID
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {officeDetails.officeId || 'Not available'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Chip 
                  icon={<CheckCircle />} 
                  label="Domain Sync: Enabled" 
                  color="success" 
                  size="small"
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleReconnect}
                disabled={connecting}
              >
                {connecting ? 'Reconnecting...' : 'Reconnect Domain'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Feed Health Dashboard */}
      {officeDetails?.connected && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Feed Health Dashboard
              </Typography>
              <Tooltip title="Refresh feed status">
                <IconButton onClick={fetchFeedStatus} disabled={loadingFeedStatus}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
            
            {loadingFeedStatus ? (
              <CircularProgress />
            ) : feedStatus ? (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Last Sync Time
                    </Typography>
                    <Typography variant="h6">
                      {feedStatus.lastSyncTime 
                        ? new Date(feedStatus.lastSyncTime).toLocaleString()
                        : 'Never'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Active Listings
                    </Typography>
                    <Typography variant="h6">
                      {feedStatus.activeListings || 0}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: feedStatus.tokenExpiresAt && new Date(feedStatus.tokenExpiresAt) < new Date(Date.now() + 24 * 60 * 60 * 1000) ? 'warning.light' : 'background.paper' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Token Expires
                    </Typography>
                    <Typography variant="h6">
                      {formatTimeUntilExpiry(feedStatus.tokenExpiresAt)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Errors
                  </Typography>
                  {feedStatus.errors && feedStatus.errors.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {feedStatus.errors.map((error: any, index: number) => (
                        <Chip
                          key={index}
                          label={error.message || error}
                          color="error"
                          size="small"
                          sx={{ mb: 1 }}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No errors
                    </Typography>
                  )}
                </Grid>
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Feed status will appear here once your connection is established.
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}
    </Container>
  );
};

export default DomainIntegration;
